import { useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode"; // Ensure you're importing correctly
import { API_URL } from "../apiConfig";

const safeJwtDecode = (token) => {
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
};

const baseUrl = `${API_URL}/auth`;
let refreshPromise = null;

const clearStoredAuth = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
};

export function useAuth() {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [userDetails, setUserDetails] = useState(() => {
    const token = localStorage.getItem("accessToken");
    return token ? safeJwtDecode(token) : null;
  });

  const refreshToken = useCallback(async () => {
    try {
      const refresh = localStorage.getItem("refreshToken");
      if (!refresh) {
        clearStoredAuth();
        setUserDetails(null);
        return false;
      }

      if (!refreshPromise) {
        refreshPromise = (async () => {
          const response = await fetch(`${baseUrl}/refresh`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ refreshToken: refresh }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          return response.json();
        })().finally(() => {
          refreshPromise = null;
        });
      }

      const data = await refreshPromise;
      if (!data?.accessToken || !data?.refreshToken) {
        throw new Error("Refresh response did not include both tokens");
      }

      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken); // Save new refresh token if provided
      setUserDetails((prev) => {
        const next = {
          ...(safeJwtDecode(data.accessToken) ?? {}),
          avatar: prev?.avatar ?? null,
        };
        return next;
      });
      return true;
    } catch (error) {
      console.error("Error refreshing token:", error);
      clearStoredAuth();
      setUserDetails(null);
      return false;
    }
  }, []);

  useEffect(() => {
    const checkTokenExpiry = async () => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const decoded = safeJwtDecode(token);
        if (!decoded) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          setUserDetails(null);
          return;
        }
        const currentTime = Date.now() / 1000; // current time in seconds
        if (decoded.exp < currentTime) {
          await refreshToken();
        } else {
          setUserDetails((prev) => ({
            ...(decoded ?? {}),
            avatar: prev?.avatar ?? null,
          }));
        }
      }
    };

    checkTokenExpiry();
  }, [refreshToken]);

  const loginUser = async (userData) => {
    try {
      setIsError(false);
      const response = await fetch(`${baseUrl}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error. Status: ${response.status}`,
        );
      }
      const data = await response.json();
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      console.log("login response avatar:", data.avatar);

      setUserDetails((prev) => ({
        ...(safeJwtDecode(data.accessToken) ?? {}),
        avatar: data.avatar ?? null,
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
      }));
      return true;
    } catch (error) {
      console.error(error);
      setIsError(true);
      setErrorMessage(error.message);
      return false;
    }
  };

const registerUser = async (userData, options = { autoLogin: true }) => {
  try {
    const formData = new FormData();

    Object.keys(userData).forEach((key) => {
      const value = userData[key];
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    const response = await fetch(`${baseUrl}/register`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorResponse = await response.text();
      throw new Error(`Failed to register user: ${errorResponse}`);
    }

    const data = await response.json();

    // ✅ if backend returns tokens on register, support that too
    if (data?.accessToken && data?.refreshToken) {
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      setUserDetails((prev) => ({
        ...(safeJwtDecode(data.accessToken) ?? {}),
        avatar: data.avatar ?? prev?.avatar ?? null,
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
      }));
      return data;
    }

    // ✅ otherwise auto-login using the credentials the user just registered with
    if (options?.autoLogin && userData?.email && userData?.password) {
      await loginUser({ email: userData.email, password: userData.password });
    }

    return data;
  } catch (error) {
    console.error("Error registering user:", error);
    setErrorMessage(error.message || "Failed to register user");
    throw error;
  }
};

  const logout = () => {
    fetch(`${baseUrl}/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refreshToken: localStorage.getItem("refreshToken"),
      }),
    }).catch(() => {});

    localStorage.removeItem("refreshToken");
    localStorage.removeItem("accessToken");
    setUserDetails(null);
  };

  const forgotPassword = async (email, captchaToken) => {
    try {
      const response = await fetch(`${baseUrl}/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, token: captchaToken }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send reset link.");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Forgot password error:", error);
      setIsError(true);
      setErrorMessage(error.message);
      return null;
    }
  };

  const resetPassword = async (resetToken, newPassword) => {
    try {
      const response = await fetch(`${baseUrl}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resetToken, newPassword }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reset password.");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Reset password error:", error);
      setIsError(true);
      setErrorMessage(error.message);
      return null;
    }
  };

  const validateResetToken = useCallback(async (resetToken) => {
    try {
      const response = await fetch(`${baseUrl}/validate-reset-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resetToken }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to validate reset token.");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Validate reset token error:", error);
      setIsError(true);
      setErrorMessage(error.message);
      return null;
    }
  }, []);

  return {
    loginUser,
    registerUser,
    refreshToken,
    logout,
    forgotPassword,
    resetPassword,
    validateResetToken,
    isError,
    errorMessage,
    userDetails,
  };
}
